import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Chart } from "@antv/g2";
import { theme } from "antd";

const SORT_LEGEND = ["Data Loss", "No Loss"];
const SCALE_COLOR = ["#FF0000", "#008000"];

export default function StateG2Chart(props) {
  const { data, containerName, shiftTime } = props;
  const [formatData, setFormatData] = useState([]);
  const chartRef = useRef();
  const { token } = theme.useToken();

  const defaultAxisStyle = {
    title: false,
    line: true,
    lineStroke: token?.colorTextBase,
    tickStroke: token?.colorTextBase,
    titleFill: token?.colorTextBase,
    labelFill: token?.colorTextBase,
    grid: true,
    gridStroke: token?.colorTextBase,
    gridStrokeOpacity: 0.6,
    tick: false,
    labelAutoHide: true,
    labelAlign: "horizontal",
    labelAutoEllipsis: true,
    labelAutoWrap: false,
    labelAutoRotate: false,
  };

  const plotChart = (dataList, shiftTime) => {
    let start = new Date(shiftTime[0]);
    let end = new Date(shiftTime[1]);
    const tooltipFmt = "YYYY-MM-DD HH:mm:ss";

    const sortedDevices = [...new Set(dataList.map((d) => d.device))].sort();

    chartRef.current.clear();

    chartRef.current
      .interval()
      .data(dataList)
      .coordinate({ transform: [{ type: "transpose" }] })
      .encode("x", "device")
      .encode("y", "value")
      .encode("color", "etype")
      .scale("x", { type: "band", padding: 0.1, domain: sortedDevices })
      .scale("y", {
        type: "time",
        domainMin: start,
        domainMax: end,
        tickInterval: calculateTickInterval(shiftTime),
        mask: "YYYY-MM-DD HH:mm",
        nice: false,
      })
      .scale("color", { range: SCALE_COLOR, domain: SORT_LEGEND })
      .axis("x", defaultAxisStyle)
      .axis("y", defaultAxisStyle)
      .tooltip((d) => ({
        value: `${dayjs(d.value[0]).format(tooltipFmt)} - ${dayjs(
          d.value[1],
        ).format(tooltipFmt)}`,
        name: d.etype,
      }))
      .legend("color", {
        position: "top",
        layout: { justifyContent: "center" },
        itemLabelFill: token?.colorTextBase,
        item: SORT_LEGEND,
      })
      .slider("y", {
        labelFormatter: (d) => dayjs(d).format("YYYY-MM-DD HH:mm"),
        handleLabelFill: token?.colorTextBase,
        selectionFill: token?.colorPrimary,
        handleFill: token?.colorPrimary,
        trackFill: token?.colorBorderSecondary,
      });

    chartRef.current?.render();
  };

  useEffect(() => {
    chartRef.current = new Chart({
      container: containerName,
      autoFit: true,
    });

    plotChart(formatData, shiftTime);
    return () => {
      chartRef?.current.destroy();
    };
  }, [formatData, token]);

  useEffect(() => {
    if (!data || !shiftTime || shiftTime.length < 2) return;

    const shiftStart = new Date(shiftTime[0]);
    const shiftEnd = new Date(shiftTime[1]);

    const formatList = data
      .filter((item) => {
        const itemStart = new Date(item.value[0]);
        const itemEnd = new Date(item.value[1]);

        return itemStart < shiftEnd && itemEnd > shiftStart;
      })
      .map((value) => {
        const rawStart = new Date(value.value[0]);
        const rawEnd = new Date(value.value[1]);

        const clippedStart = rawStart < shiftStart ? shiftStart : rawStart;
        const clippedEnd = rawEnd > shiftEnd ? shiftEnd : rawEnd;

        return {
          ...value,
          etype: getStateName(value.etype),
          value: [clippedStart, clippedEnd],
        };
      })
      .sort((a, b) => {
        const aIndex = SORT_LEGEND.indexOf(a.etype);
        const bIndex = SORT_LEGEND.indexOf(b.etype);
        return aIndex - bIndex;
      });

    setFormatData(formatList);
  }, [data, shiftTime]);

  return (
    <div id={containerName} style={{ width: "100%", height: "100%" }}></div>
  );
}

function getStateName(name) {
  switch (name) {
    case "-1":
      return "Unassigned";
    case "0":
      return "No Loss";
    case "1":
      return "Data Loss";
    default:
      return "Unassigned";
  }
}

function calculateTickInterval(shiftTime) {
  let days = dayjs(shiftTime[1]).diff(dayjs(shiftTime[0]), "day");

  if (days < 1) {
    return 3600000;
  } else if (days >= 1 && days <= 10) {
    return 43200000;
  } else if (days > 10 && days <= 31) {
    return 86400000;
  } else if (days > 31 && days <= 182) {
    return 604800000;
  } else if (days > 182 && days <= 366) {
    return 2592000000;
  } else {
    return 31536000000;
  }
}
